'use client';
/**
 * Descout dashboard — shared UI atoms.
 *
 * 'use client' is required for: Card (hover state), MiniSpark (canvas +
 * useEffect), AreaChart (SVG ids are stable client-side), useIsMobile.
 *
 * Pure formatters live in ./format.ts (no 'use client') for RSC pages.
 */

import { entityUrl } from '@/lib/entity-url';
import React, {
  useState,
  useRef,
  useEffect,
  CSSProperties,
  ReactNode,
  MouseEvent,
} from 'react';
import Link from 'next/link';
import {
  LayoutGrid,
  BarChart3,
  Network,
  Activity,
  Newspaper,
  BookOpen,
  Search,
  Plus,
  LogIn,
  ChevronDown,
  PanelLeft,
  ArrowUpRight,
  HelpCircle,
} from 'lucide-react';

// Single import from ./format — used in component bodies below.
// (server-side callers should import directly from ./format.ts)
import {
  fmtPrice,
  fmtFloor,
  CARD,
  ENTITY_TYPES,
  initials,
  type EntityTypeKey,
  type EntityTypeEntry,
} from './format';
import PriceChange from '@/components/hub/PriceChange';
import TokenImage from '@/components/ui/TokenImage';
import { TokenSparklineCompact } from '@/components/market/TokenSparkline';
import type { ChainTokenRow, ChainNftRow } from '@/types/hub';

// Public re-exports for convenience consumers
export { fmtPrice, ENTITY_TYPES, initials };
export type { EntityTypeKey, EntityTypeEntry };

/* ─────────────────────────────────────────────────────────────────────────────
   Icon — maps prototype icon names to lucide-react equivalents.
   strokeWidth ~1.75 for readable monochrome marks.
───────────────────────────────────────────────────────────────────────────── */
export type IconName =
  | 'overview'
  | 'market'
  | 'entities'
  | 'social'
  | 'news'
  | 'read'
  | 'questions'
  | 'search'
  | 'plus'
  | 'signin'
  | 'chevron'
  | 'collapse'
  | 'arrowUpRight';

const ICON_MAP: Record<IconName, React.ElementType> = {
  overview:     LayoutGrid,
  market:       BarChart3,
  entities:     Network,
  social:       Activity,
  news:         Newspaper,
  read:         BookOpen,
  questions:    HelpCircle,
  search:       Search,
  plus:         Plus,
  signin:       LogIn,
  chevron:      ChevronDown,
  collapse:     PanelLeft,
  arrowUpRight: ArrowUpRight,
};

export interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
  style?: CSSProperties;
  className?: string;
}

export function Icon({
  name,
  size = 16,
  color = 'currentColor',
  strokeWidth = 1.75,
  style,
  className,
}: IconProps) {
  const Comp = ICON_MAP[name];
  return (
    <Comp
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      style={style}
      className={className}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Eyebrow — uppercase mono label (amber by default)
───────────────────────────────────────────────────────────────────────────── */
export interface EyebrowProps {
  children: ReactNode;
  color?: string;
  style?: CSSProperties;
}

export function Eyebrow({
  children,
  color = 'var(--amber)',
  style,
}: EyebrowProps) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Mono — monospaced text atom
───────────────────────────────────────────────────────────────────────────── */
export interface MonoProps {
  children: ReactNode;
  color?: string;
  size?: number;
  weight?: number;
  style?: CSSProperties;
}

export function Mono({
  children,
  color = 'var(--parchment)',
  size = 12,
  weight = 400,
  style,
}: MonoProps) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: size,
        fontWeight: weight,
        color,
        letterSpacing: '0.02em',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Card — surface tile with optional hover / spine / glow.
   Uses display:contents inner wrapper so flex children work correctly.
───────────────────────────────────────────────────────────────────────────── */
export interface CardProps {
  children: ReactNode;
  style?: CSSProperties;
  /** Subtle radial glow in bottom-right corner */
  glow?: boolean;
  /** 3px amber left border accent */
  spine?: boolean;
  /** Amber border on hover */
  hover?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  className?: string;
}

export function Card({
  children,
  style,
  glow = false,
  spine = false,
  hover = false,
  onClick,
  className,
}: CardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={className}
      style={{
        position: 'relative',
        background: 'var(--surface)',
        border: '0.5px solid var(--divider)',
        borderRadius: 4,
        borderLeft: spine ? '3px solid var(--amber)' : undefined,
        borderColor: hover && hovered ? 'var(--amber-line)' : 'var(--divider)',
        // Re-pin the left spine color so the blanket borderColor override above
        // doesn't clobber it when both `spine` and `hover` are active.
        borderLeftColor: spine ? 'var(--amber)' : undefined,
        transition: 'border-color 180ms',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        ...style,
      }}
    >
      {glow && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(ellipse 60% 80% at 88% 100%, rgba(232,168,50,0.07) 0%, transparent 70%)',
          }}
        />
      )}
      <div style={{ display: 'contents' }}>{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Avatar — initials tile; token type gets amber-dim bg
───────────────────────────────────────────────────────────────────────────── */
export interface AvatarProps {
  name: string;
  type?: EntityTypeKey | string;
  size?: number;
  /** true = 4px radius square (default), false = circle */
  square?: boolean;
}

export function Avatar({ name, type, size = 36, square = true }: AvatarProps) {
  const isToken = type === 'token';
  const borderRadius = square ? 4 : '50%';

  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isToken ? 'var(--amber-dim)' : 'var(--surface-2)',
        border: '0.5px solid var(--divider)',
        color: isToken ? 'var(--amber)' : 'var(--parchment-dim)',
        fontFamily: 'var(--font-sans)',
        fontWeight: 700,
        fontSize: size * 0.34,
        letterSpacing: '-0.02em',
      }}
    >
      {initials(name)}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   TypeBadge — small uppercase mono entity-type label
───────────────────────────────────────────────────────────────────────────── */
export interface TypeBadgeProps {
  type: EntityTypeKey | string;
}

export function TypeBadge({ type }: TypeBadgeProps) {
  const entry = ENTITY_TYPES[type as EntityTypeKey] ?? { label: type };

  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 9.5,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'var(--slate)',
        background: 'var(--parchment-faint)',
        padding: '3px 6px',
        borderRadius: 2,
      }}
    >
      {entry.label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SectionHeader — THE universal section header: optional mono eyebrow + h2 at
   ONE locked size + optional right slot + CTA.

   CTA renders as a crawlable <Link> when `href` is given (RSC pages pass only
   serializable props), or as a <button onCta> for client surfaces.
───────────────────────────────────────────────────────────────────────────── */
export interface SectionHeaderProps {
  title: string;
  /** Optional mono uppercase eyebrow above the title (universal eyebrow style). */
  eyebrow?: string;
  cta?: string;
  /** Crawlable CTA link target (preferred — usable from server components). */
  href?: string;
  /** Client-side CTA callback (used only when `href` is absent). */
  onCta?: () => void;
  right?: ReactNode;
}

const SECTION_CTA_STYLE: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--amber)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

export function SectionHeader({ title, eyebrow, cta, href, onCta, right }: SectionHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        marginBottom: 16,
        gap: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        {eyebrow && (
          <Eyebrow style={{ display: 'block', marginBottom: 4 }}>{eyebrow}</Eyebrow>
        )}
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-sans)',
            fontSize: 19,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--parchment)',
          }}
        >
          {title}
        </h2>
      </div>
      {right}
      {cta &&
        (href ? (
          <Link href={href} style={SECTION_CTA_STYLE}>
            {cta} <span style={{ fontSize: 12 }}>→</span>
          </Link>
        ) : (
          <button onClick={onCta} style={SECTION_CTA_STYLE}>
            {cta} <span style={{ fontSize: 12 }}>→</span>
          </button>
        ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   StatTile — KPI tile: mono label + mono value + optional delta (PriceChange).
   A read on activity — neutral framing. Render only when its datum is present.
───────────────────────────────────────────────────────────────────────────── */
export function StatTile({
  label,
  value,
  deltaPct,
}: {
  label: string;
  value: string;
  deltaPct?: number | null;
}) {
  return (
    <div className={`${CARD} px-3 py-2`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-parchment-dim truncate">
        {label}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1.5">
        <span className="font-mono text-sm font-semibold text-parchment tracking-tight">
          {value}
        </span>
        {typeof deltaPct === 'number' && (
          <PriceChange value={deltaPct} className="text-[10px]" />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   TokenRow — token list row (rank · logo · $SYMBOL/name · spark · price/Δ).
   Every number is Mono; the delta goes through PriceChange.
───────────────────────────────────────────────────────────────────────────── */
export function TokenRow({ t }: { t: ChainTokenRow }) {
  const href = t.tagSlug
    ? entityUrl(t.tagSlug, { trailingSlash: false, hash: 'market' })
    : `/coins/${t.symbol.toLowerCase()}`;
  return (
    <Link
      href={href}
      className="group flex items-center gap-3 py-2.5 border-b border-divider last:border-b-0 first:pt-0"
    >
      <span className="font-mono text-[10px] text-parchment-dim w-4 text-right flex-shrink-0">
        {t.rank}
      </span>
      <TokenImage symbol={t.symbol} imageUrl={t.imageUrl} size={22} className="rounded-full flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[13px] font-semibold text-parchment truncate">${t.symbol}</div>
        <div className="font-mono text-[9px] text-parchment-dim truncate">{t.name}</div>
      </div>
      <div className="hidden sm:block flex-shrink-0">
        <TokenSparklineCompact symbol={t.symbol} prices={t.spark} isUp={(t.change24h ?? 0) >= 0} />
      </div>
      <div className="text-right flex-shrink-0 w-[5.5rem]">
        <div className="font-mono text-[13px] text-parchment">{fmtPrice(t.price)}</div>
        <PriceChange value={t.change24h} showArrow={false} className="text-[11px]" />
      </div>
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   NftRow — NFT collection list row (thumb · name · floor/Δ).
───────────────────────────────────────────────────────────────────────────── */
export function NftRow({ n }: { n: ChainNftRow }) {
  return (
    <Link
      href={n.href}
      className="group flex items-center gap-3 py-2.5 border-b border-divider last:border-b-0 first:pt-0"
    >
      {n.image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={n.image} alt="" loading="lazy" className="w-9 h-9 rounded object-cover flex-shrink-0" />
      ) : (
        <span className="w-9 h-9 rounded bg-surface2 border-[0.5px] border-divider flex-shrink-0" />
      )}
      <span className="font-display text-[13px] font-semibold text-parchment truncate min-w-0 flex-1">
        {n.name}
      </span>
      <div className="text-right flex-shrink-0">
        <div className="font-mono text-[13px] text-parchment">{fmtFloor(n.floorPrice, n.floorSymbol)}</div>
        <PriceChange value={n.change24h} showArrow={false} className="text-[11px]" />
      </div>
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Skeleton — THE loading/placeholder block (no more hand-rolled pulse divs).
   Size it via className (h-N / w-N utilities) or style.
───────────────────────────────────────────────────────────────────────────── */
export function Skeleton({
  className = '',
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      aria-hidden
      className={`animate-pulse rounded bg-surface2 ${className}`}
      style={style}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MiniSpark — canvas sparkline (client only; uses canvas API + devicePixelRatio)
───────────────────────────────────────────────────────────────────────────── */
export interface MiniSparkProps {
  data: number[];
  /** true = green (price up), false = red (price down) */
  up?: boolean;
  width?: number;
  height?: number;
}

export function MiniSpark({ data, up = true, width = 64, height = 24 }: MiniSparkProps) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || data.length < 2) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const min = Math.min(...data);
    const max = Math.max(...data);
    const rng = (max - min) || 1;
    const pad = 2;

    const xs = (i: number) => pad + (i / (data.length - 1)) * (width - pad * 2);
    const ys = (v: number) => height - pad - ((v - min) / rng) * (height - pad * 2);

    const col = up ? 'rgba(85,201,133,1)' : 'rgba(224,92,74,1)';

    // area fill
    ctx.beginPath();
    ctx.moveTo(xs(0), ys(data[0]));
    data.forEach((v, i) => ctx.lineTo(xs(i), ys(v)));
    ctx.lineTo(xs(data.length - 1), height);
    ctx.lineTo(xs(0), height);
    ctx.closePath();

    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, up ? 'rgba(85,201,133,0.20)' : 'rgba(224,92,74,0.20)');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fill();

    // line
    ctx.beginPath();
    ctx.moveTo(xs(0), ys(data[0]));
    data.forEach((v, i) => ctx.lineTo(xs(i), ys(v)));
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.4;
    ctx.lineJoin = 'round';
    ctx.stroke();
  }, [data, up, width, height]);

  return <canvas ref={ref} style={{ width, height, display: 'block' }} />;
}

/* ─────────────────────────────────────────────────────────────────────────────
   AreaChart — SVG area chart (TVL hero / macro trend)
───────────────────────────────────────────────────────────────────────────── */
export interface AreaChartProps {
  data: number[];
  height?: number;
  color?: string;
  /** RGB triplet e.g. '232,168,50' — used for gradient stops */
  accentRGB?: string;
}

export function AreaChart({
  data,
  height = 160,
  color = 'var(--amber)',
  accentRGB = '232,168,50',
}: AreaChartProps) {
  const W = 720;
  const P = 24;

  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const rng = (max - min) || 1;

  const xs = (i: number) => P + (i / (data.length - 1)) * (W - P * 2);
  const ys = (v: number) => P + (1 - (v - min) / rng) * (height - P * 2);

  const line = data
    .map((v, i) => `${i ? 'L' : 'M'}${xs(i).toFixed(1)},${ys(v).toFixed(1)}`)
    .join(' ');
  const area = `${line} L${xs(data.length - 1).toFixed(1)},${height - P} L${xs(0).toFixed(1)},${height - P} Z`;

  // Stable-but-unique gradient ID: fold in height so two charts with the
  // same accentRGB at different heights don't share a <defs> id on one page.
  const gid = 'ag' + accentRGB.replace(/[^0-9]/g, '') + '_' + height;

  const gridYs = [0, 0.5, 1].map((g) => P + g * (height - P * 2));

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={`rgba(${accentRGB},0.28)`} />
          <stop offset="100%" stopColor={`rgba(${accentRGB},0)`} />
        </linearGradient>
      </defs>
      {gridYs.map((y, i) => (
        <line
          key={i}
          x1={P}
          x2={W - P}
          y1={y}
          y2={y}
          stroke="var(--divider)"
          strokeWidth="0.5"
          strokeDasharray="2 4"
        />
      ))}
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" />
      <circle
        cx={xs(data.length - 1)}
        cy={ys(data[data.length - 1])}
        r="3.2"
        fill={color}
      />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   useIsMobile — breakpoint hook (client only)
───────────────────────────────────────────────────────────────────────────── */
export function useIsMobile(bp = 820): boolean {
  // Initialize to a STABLE server value (false) so the server-rendered tree
  // (always desktop) matches the first client render — no hydration mismatch.
  // The real viewport width is read once on mount + on resize. This accepts a
  // one-frame desktop-first paint on mobile, which is the correct tradeoff.
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < bp);
    handler(); // read real width once on mount
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);

  return mobile;
}
